// src/pages/AgendaForm.js
import React, { useEffect, useState } from "react";
import FormLayout from "../layouts/FormLayout"; // project FormLayout
import * as FS from "../styles/formStyles";
import { apiFetch } from "../utils/wkCommon"; // small wrapper: uses Authorization header if JWT present

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SERVICE_ROLE_ZH = {
  Caochi: "操持",
  Shengge: "聖歌",
  Penceramah: "授課"
};

function Field({ label, children, hint }) {
  return (
    <div className="w-full">
      <label style={FS.label}>{label}</label>
      <div className="mt-1">{children}</div>
      {hint && <p className="mt-1" style={FS.smallMuted}>{hint}</p>}
    </div>
  );
}

function todayISO() {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

export default function AgendaFormPage({ agendamaster = null, agendaId = null }) {
  const [loading, setLoading] = useState(false);
  const [agenda, setAgenda] = useState({
    subject: "",
    agendaDate: todayISO(),
    agendaDateEnd: "",
    hoursInfo: "",
    allowPreRegistration: false,
    allowPreRegistrationUmatExternal: false,
    FaHui: false,
    Highlight: false,
    oneTimeTagNumber: false,
    area: null,
    category_age_groups: [],
    audienceDutyLevelMinimum: null,
    agenda_master: null,
  });

  const [personnels, setPersonnels] = useState([]);
  const [errors, setErrors] = useState(null);

  // load AgendaMaster if provided via URL param (agendamaster or agendaMasterId)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const paramId = params.get("agendamaster") || params.get("agendaMasterId") || params.get("agenda_master");
    if (!agendaId && paramId) {
      loadAgendaMaster(paramId);
    }
  }, []);

  useEffect(() => {
    if (agendaId) loadAgenda(agendaId);
    else if (agendamaster) {
      applyAgendaMasterDefaults(agendamaster);
    }
  }, [agendaId, agendamaster]);

  async function loadAgendaMaster(id) {
    setLoading(true);
    try {
      const url = `${API_URL}/api/agenda-masters/${encodeURIComponent(id)}?populate=*`;
      const json = await apiFetch(url);
      // Strapi v5: { data: { id, attributes... } } or sometimes direct object depending wrapper — be tolerant
      const am = json?.data ?? json;
      if (am) {
        applyAgendaMasterDefaults(am);
        setAgenda((a) => ({ ...a, agenda_master: am.id ?? am?.id ?? id }));
      }
    } catch (e) {
      console.error("Error loading AgendaMaster", e);
      setErrors(String(e));
    } finally {
      setLoading(false);
    }
  }

  function applyAgendaMasterDefaults(am) {
    // Copy commonly named fields from agendamaster to agenda (if present). Values remain editable.
    setAgenda((a) => ({
      ...a,
      subject: am.name || am.subject || a.subject,
      allowPreRegistration: typeof am.allowPreRegistration !== "undefined" ? !!am.allowPreRegistration : a.allowPreRegistration,
      allowPreRegistrationUmatExternal:
        typeof am.allowPreRegistrationUmatExternal !== "undefined" ? !!am.allowPreRegistrationUmatExternal : a.allowPreRegistrationUmatExternal,
      FaHui: typeof am.FaHui !== "undefined" ? !!am.FaHui : a.FaHui,
      oneTimeTagNumber: typeof am.oneTimeTagNumber !== "undefined" ? !!am.oneTimeTagNumber : a.oneTimeTagNumber,
      area: am.area?.id ?? am.area ?? a.area,
      category_age_groups: (am.category_age_groups || []).map((g) => g.id ?? g) || a.category_age_groups,
      audienceDutyLevelMinimum:
        typeof am.audienceDutyLevelMinimum !== "undefined" ? am.audienceDutyLevelMinimum : a.audienceDutyLevelMinimum,
    }));
  }

  async function loadAgenda(id) {
    setLoading(true);
    try {
      const url =
        `${API_URL}/api/agendas/${encodeURIComponent(id)}` +
        `?populate[0]=agenda_book` +
        `&populate[1]=agenda_master` +
        `&populate[2]=area` +
        `&populate[3]=category_age_groups`;
      const data = await apiFetch(url);
      if (data?.data) {
        const at = data.data;
        setAgenda({
          subject: at.subject || "",
          agendaDate: at.agendaDate || todayISO(),
          agendaDateEnd: at.agendaDateEnd || "",
          hoursInfo: at.hoursInfo || "",
          allowPreRegistration: !!at.allowPreRegistration,
          allowPreRegistrationUmatExternal: !!at.allowPreRegistrationUmatExternal,
          FaHui: !!at.FaHui,
          Highlight: !!at.Highlight,
          oneTimeTagNumber: !!at.oneTimeTagNumber,
          area: at.area?.id ?? null,
          category_age_groups: (at.category_age_groups || []).map((g) => g.id),
          audienceDutyLevelMinimum: at.audienceDutyLevelMinimum ?? null,
          agenda_master: at.agenda_master?.id ?? null,
          id: at.id,
          agenda_book: at.agenda_book || null,
        });

        const pers =
          (at?.agenda_book?.agendaPersonnels?.GroupRole || [])
            .at(0)?.ServiceRole
            ?.map((r, i) => ({
              id: i,
              serviceRole: r.Label_id || "",
              roleOtherDesc: "",
              subject: "",
              seqno: r.Row || i + 1,
              InternalUmat: r.InternalUmat || [],
              ExternalUmat: r.ExternalUmat || [],
              ExternalNonUmat: r.ExternalNonUmat || [],
              SeparateLineOfGender: !!r.SeparateLineOfGender
            })) || [];
        setPersonnels(pers);
      } else {
        setErrors("Agenda not found");
      }
    } catch (e) {
      console.error(e);
      setErrors(String(e));
    } finally {
      setLoading(false);
    }
  }

  function setField(field, value) {
    setAgenda((a) => ({ ...a, [field]: value }));
  }

  function addPersonnelTemplate() {
    return {
      serviceRole: "Caochi",
      roleOtherDesc: "",
      subject: "",
      seqno: personnels.length + 1,
      InternalUmat: [],
      ExternalUmat: [],
      ExternalNonUmat: [],
      SeparateLineOfGender: false,
    };
  }

  function addPersonnel(pref = {}) {
    setPersonnels((p) => [...p, { ...addPersonnelTemplate(), ...pref }]);
  }

  function updatePersonnel(index, changes) {
    setPersonnels((p) => p.map((item, i) => (i === index ? { ...item, ...changes } : item)));
  }

  function removePersonnel(index) {
    setPersonnels((p) => p.filter((_, i) => i !== index));
  }

  function quickAddRole(role) {
    addPersonnel({ serviceRole: role });
  }

  async function saveAgenda(e) {
    if (e && typeof e.preventDefault === "function") e.preventDefault();
    setLoading(true);
    setErrors(null);
    try {
      const payload = {
        data: {
          subject: agenda.subject,
          agendaDate: agenda.agendaDate || null,
          agendaDateEnd: agenda.agendaDateEnd || null,
          hoursInfo: agenda.hoursInfo,
          allowPreRegistration: !!agenda.allowPreRegistration,
          allowPreRegistrationUmatExternal: !!agenda.allowPreRegistrationUmatExternal,
          FaHui: !!agenda.FaHui,
          Highlight: !!agenda.Highlight,
          oneTimeTagNumber: !!agenda.oneTimeTagNumber,
          area: agenda.area ? Number(agenda.area) : null,
          category_age_groups: agenda.category_age_groups || [],
          audienceDutyLevelMinimum: agenda.audienceDutyLevelMinimum ? Number(agenda.audienceDutyLevelMinimum) : null,
          agenda_master: agenda.agenda_master ? Number(agenda.agenda_master) : null,
        },
      };

      let createdAgendaId = agenda.id || null;

      if (agenda.id) {
        const url = `${API_URL}/api/agendas/${agenda.id}`;
        const resp = await apiFetch(url, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const r = resp;
        createdAgendaId = r?.data?.id ?? agenda.id;
      } else {
        const url = `${API_URL}/api/agendas`;
        const resp = await apiFetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const r = resp;
        createdAgendaId = r?.data?.id;
      }

      const existingBookId =
        agenda?.agenda_book?.documentId ||
        agenda?.agenda_book?.id ||
        null;

      const personnelJson = {
        Print: "all",
        Remarks: "",
        GroupRole: [
          {
            Row: 1,
            Label_id: "General",
            Label_zh: "普通",
            ServiceRole: personnels.map((p, i) => ({
              Col: 1,
              Row: p.seqno || (i + 1),
              Width: "full",
              Label_id: p.serviceRole,
              Label_zh: SERVICE_ROLE_ZH[p.serviceRole] || "",
              InternalUmat: p.InternalUmat || [],
              ExternalUmat: p.ExternalUmat || [],
              ExternalNonUmat: p.ExternalNonUmat || [],
              SeparateLineOfGender: !!p.SeparateLineOfGender
            })),
            ShowOnReport: false
          }
        ]
      };

      const bookPayload = {
        data: {
          agenda: createdAgendaId,
          agendaPersonnels: personnelJson
        }
      };

      if (existingBookId) {
        await apiFetch(`/api/agenda-books/${existingBookId}`, {
          method: "PUT",
          body: bookPayload
        });
      } else {
        await apiFetch(`/api/agenda-books`, {
          method: "POST",
          body: bookPayload
        });
      }

      alert("Agenda saved");
    } catch (err) {
      console.error(err);
      setErrors(String(err));
      alert("Error saving: " + String(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <FormLayout title={agendaId ? "Edit Agenda" : "Create Agenda"} onSubmit={saveAgenda} submitting={loading}>
      {/* FormLayout is expected to render a submit button and call onSubmit(event).
          If your FormLayout expects a different prop name, change 'onSubmit' accordingly. */}
      <div className="space-y-6 p-6 bg-white shadow-sm rounded-md">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            <Field label="Subject">
              <input
                aria-label="Subject"
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                style={FS.input}
                value={agenda.subject}
                onChange={(e) => setField("subject", e.target.value)}
                placeholder="Judul acara"
              />
            </Field>

            <div className="grid grid-cols-2 gap-4">
              <Field label="Agenda Date">
                <input
                  type="date"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm"
                  style={FS.input}
                  value={agenda.agendaDate || ""}
                  onChange={(e) => setField("agendaDate", e.target.value)}
                />
              </Field>
              <Field label="Agenda Date End">
                <input
                  type="date"
                  className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm"
                  style={FS.input}
                  value={agenda.agendaDateEnd || ""}
                  onChange={(e) => setField("agendaDateEnd", e.target.value)}
                />
              </Field>
            </div>

            <Field label="Hours Info" hint="Contoh: 09:00 - 11:00">
              <input
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm"
                style={FS.input}
                value={agenda.hoursInfo || ""}
                onChange={(e) => setField("hoursInfo", e.target.value)}
                placeholder="Jam acara"
              />
            </Field>

            <Field label="Agenda Master (ID)">
              <input
                className="mt-1 block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm"
                style={FS.input}
                value={agenda.agenda_master || ""}
                onChange={(e) => setField("agenda_master", e.target.value)}
                placeholder="Isi ID agendamaster jika ada (boleh dikosongkan)"
              />
            </Field>

            <div className="flex flex-wrap gap-3 mt-2">
              <label className="inline-flex items-center space-x-2">
                <input type="checkbox" checked={agenda.allowPreRegistration} onChange={(e) => setField("allowPreRegistration", e.target.checked)} className="h-4 w-4" />
                <span className="text-sm">Allow Pre Registration</span>
              </label>
              <label className="inline-flex items-center space-x-2">
                <input type="checkbox" checked={agenda.FaHui} onChange={(e) => setField("FaHui", e.target.checked)} className="h-4 w-4" />
                <span className="text-sm">FaHui</span>
              </label>
              <label className="inline-flex items-center space-x-2">
                <input type="checkbox" checked={agenda.Highlight} onChange={(e) => setField("Highlight", e.target.checked)} className="h-4 w-4" />
                <span className="text-sm">Highlight</span>
              </label>
            </div>
          </div>

          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-md border border-gray-100">
              <h4 className="text-sm font-semibold mb-2">Quick Actions</h4>
              <div className="flex flex-col gap-2">
                <button type="button" onClick={() => quickAddRole("Caochi")} className="w-full inline-flex justify-center items-center px-3 py-2 border rounded-md text-sm font-medium hover:bg-gray-100" style={FS.btnNeutral}>Add Caochi</button>
                <button type="button" onClick={() => quickAddRole("Shengge")} className="w-full inline-flex justify-center items-center px-3 py-2 border rounded-md text-sm font-medium hover:bg-gray-100" style={FS.btnNeutral}>Add Shengge</button>
                <button type="button" onClick={() => quickAddRole("Penceramah")} className="w-full inline-flex justify-center items-center px-3 py-2 border rounded-md text-sm font-medium hover:bg-gray-100" style={FS.btnNeutral}>Add Penceramah</button>
                <button type="button" onClick={() => addPersonnel()} className="w-full inline-flex justify-center items-center px-3 py-2 bg-white border border-dashed rounded-md text-sm font-medium hover:bg-gray-50" style={FS.btnNeutral}>Add Blank Personnel</button>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-md border border-gray-100">
              <h4 className="text-sm font-semibold mb-2">Metadata</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <label className="text-sm w-32">Area</label>
                  <input className="flex-1 rounded-md border border-gray-300 px-2 py-1" style={FS.input} value={agenda.area || ""} onChange={(e) => setField("area", e.target.value)} placeholder="ID area" />
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-sm w-32">Audience Min</label>
                  <input type="number" className="flex-1 rounded-md border border-gray-300 px-2 py-1" style={FS.input} value={agenda.audienceDutyLevelMinimum || ""} onChange={(e) => setField("audienceDutyLevelMinimum", e.target.value)} placeholder="Minimum duty level" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr />

        <div>
          <h3 className="text-lg font-semibold">Agenda Personnel</h3>
          <p className="text-sm text-gray-500">Tambah atau ubah personnel — gunakan tombol quick-add untuk peran yang sering dipakai.</p>

          <div className="mt-4 space-y-3">
            {personnels.length === 0 && <div className="text-sm text-gray-400">Belum ada personnel.</div>}
            {personnels.map((p, idx) => (
              <div key={idx} className="p-4 border rounded-md shadow-sm bg-white flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <div className="font-semibold">#{p.seqno || idx + 1} — {p.serviceRole}{p.serviceRole === 'Other' && p.roleOtherDesc ? ` (${p.roleOtherDesc})` : ''}</div>
                    <div className="flex items-center gap-2">
                      <button type="button" onClick={() => removePersonnel(idx)} className="text-red-600 text-sm">Remove</button>
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs text-gray-600">Service Role</label>
                      <select className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1" value={p.serviceRole} onChange={(e) => updatePersonnel(idx, { serviceRole: e.target.value })}>
                        <option value="Caochi">Caochi</option>
                        <option value="Shengge">Shengge</option>
                        <option value="Penceramah">Penceramah</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">If Other, describe</label>
                      <input className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1" style={FS.input} value={p.roleOtherDesc} onChange={(e) => updatePersonnel(idx, { roleOtherDesc: e.target.value })} placeholder="contoh: Penceramah" />
                    </div>
                    <div>
                      <label className="text-xs text-gray-600">Subject</label>
                      <input className="mt-1 block w-full rounded-md border border-gray-300 px-2 py-1" style={FS.input} value={p.subject} onChange={(e) => updatePersonnel(idx, { subject: e.target.value })} placeholder="Topik / judul" />
                    </div>
                  </div>

                  <div className="mt-3 flex items-center gap-3">
                    <div>
                      <label className="text-xs text-gray-600">Seqno</label>
                      <input type="number" className="mt-1 block w-28 rounded-md border border-gray-300 px-2 py-1" style={FS.input} value={p.seqno} onChange={(e) => updatePersonnel(idx, { seqno: Number(e.target.value) })} />
                    </div>
                  </div>
                </div>

                <div className="md:w-48 flex flex-col gap-2">
                  <button type="button" onClick={() => updatePersonnel(idx, { seqno: (personnels.length + 1) })} className="px-3 py-2 border rounded-md text-sm">Set seqno end</button>
                  <button type="button" onClick={() => updatePersonnel(idx, { seqno: 1 })} className="px-3 py-2 border rounded-md text-sm">Move to first</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {errors && <div className="text-red-600">{errors}</div>}
      </div>
    </FormLayout>
  );
}
